import React, { useEffect, useRef } from "react";
import { Modal, Button, Form, InputGroup } from "react-bootstrap";
import swal from "sweetalert";
import Select from "react-select";
import { selectCustomStyles } from "../../../../assets/global/selectCustomStyles";
import CustomDatePicker from "../../../../components/CustomDatePicker";
import TypedDateInput from "../../../../components/inputs/TypedDateInput";
import useDarkMode from "../../../../components/Themes/Usedarkmode";
import { formatPeriod } from "../../../../helper/format_period";
import {
  formatNumberWithComma,
  formatToPHP,
  isValidDecimal,
  parseNumber,
  sanitizeAmount,
} from "../../../../utils/numberFormatter";
import { getNetIncomeAvailable } from "../../../../services/ProfitDistribution/getNetIncomeAvailable.service";

const toCents = (value) => Math.round(parseNumber(value) * 100);

// Why the amount to distribute can't be saved, or null when it's fine. A net
// loss has nothing to distribute, so the amount isn't checked then. The
// server checks the same rules again.
export const getAmountToDistributeError = (form) => {
  if (form.netIncomeAvailable === null) {
    return "The net income available is still loading.";
  }
  const netCents = toCents(form.netIncomeAvailable);
  if (netCents <= 0) return null;

  const amountCents = toCents(form.amountToDistribute);
  if (amountCents <= 0) return "The amount to distribute must be more than 0.";
  if (amountCents > netCents) {
    return `The amount to distribute can't be more than the net income available of ${formatToPHP(
      form.netIncomeAvailable,
    )}.`;
  }
  return null;
};

// The whole net income, as the amount input's default value
const toAmountInput = (netIncome) =>
  netIncome > 0 ? (toCents(netIncome) / 100).toFixed(2) : "0";

// Date input for CustomDatePicker: typed as MM/dd/yyyy and shown as Oct/10/2026
// (components/inputs/TypedDateInput), or picked from the calendar. Only a
// COMPLETE date is handed over, so a half-typed date (e.g. 10/09/2) never
// reaches the form; a cleared field snaps back to the last date on blur.
const TypeableDateInput = React.forwardRef(
  (
    {
      value,
      onClick,
      onChange,
      onBlur,
      onFocus,
      onKeyDown,
      generateYears,
      disabled,
    },
    ref,
  ) => {
    return (
      <TypedDateInput
        className="form-control p-2 w-100"
        style={{ cursor: disabled ? "not-allowed" : "text" }}
        onClick={(e) => {
          if (disabled) return; // Prevent clicking when disabled
          if (onClick) onClick(e);

          let selectedYear = new Date().getFullYear();
          if (value) {
            const parsed = new Date(value);
            // Extract year safely if the value is a valid date
            if (!isNaN(parsed.getTime())) {
              selectedYear = parsed.getFullYear();
            }
          }

          // Reset/Initialize Year List based on selected date
          if (generateYears && typeof generateYears === "function") {
            generateYears(selectedYear);
          }
        }}
        onChange={(e) => e.target.value && onChange(e)}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        value={value}
        ref={ref}
        required
        disabled={disabled}
      />
    );
  },
);

TypeableDateInput.displayName = "TypeableDateInput";

function AddProfitDistributionModal({
  show,
  onClose,
  onSubmit,
  form,
  setForm,
  onFieldChange,
  validated,
  retainedNetIncomeOptions,
  isLoading,
  isSaving,
  title = "Add Profit Distribution",
  submitLabel = "Add",
}) {
  const { themeSelectStyles } = useDarkMode();

  // Net income of the selected Retained Net Incomes. amountToDistribute null
  // means "not entered yet": it then takes the whole net income once loaded.
  const selectedIds = form.selectedRetainedNetIncomes
    .map((option) => option.value)
    .join(",");
  const requestRef = useRef(0);

  useEffect(() => {
    if (!show) return;
    const requestId = ++requestRef.current;

    if (!selectedIds) {
      setForm((prev) => ({ ...prev, netIncomeAvailable: 0 }));
      return;
    }

    setForm((prev) => ({ ...prev, netIncomeAvailable: null }));
    getNetIncomeAvailable(selectedIds.split(","))
      .then((netIncome) => {
        // A newer selection was made while this one was loading
        if (requestId !== requestRef.current) return;
        setForm((prev) => ({
          ...prev,
          netIncomeAvailable: netIncome,
          amountToDistribute:
            prev.amountToDistribute === null
              ? toAmountInput(netIncome)
              : prev.amountToDistribute,
        }));
      })
      .catch((error) => {
        console.log(error);
        if (requestId !== requestRef.current) return;
        swal({
          icon: "error",
          title: "Something went wrong",
          text: "Unable to compute the net income available.",
        });
      });
  }, [show, selectedIds]);

  const netIncomeAvailable = form.netIncomeAvailable;
  const isNetIncomeLoading = netIncomeAvailable === null;
  const hasNetIncome = !isNetIncomeLoading && netIncomeAvailable > 0;
  const amountError =
    validated && hasNetIncome ? getAmountToDistributeError(form) : null;

  const handleAmountChange = (value) => {
    const sanitized = sanitizeAmount(value);
    if (!isValidDecimal(sanitized, 2)) return;
    onFieldChange("amountToDistribute", sanitized);
  };

  return (
    <Modal
      show={show}
      backdrop="static"
      keyboard={false}
      onHide={onClose}
      size="lg">
      <Form noValidate onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>{title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row mb-3">
            <div className="col-sm">
              <Form.Label className="fw-bold">
                Retained Net Income <span className="text-danger">*</span>
              </Form.Label>
              <div
                onMouseDown={(e) => {
                  if (!isLoading && retainedNetIncomeOptions.length === 0) {
                    e.preventDefault();
                    swal({
                      icon: "warning",
                      title: "No Retained Net Income Found",
                      text: "No available Retained Net Income. Please create one in Cutoff > Retained Net Income first.",
                    });
                  }
                }}>
                <Select
                  isMulti
                  options={retainedNetIncomeOptions}
                  value={form.selectedRetainedNetIncomes}
                  onChange={(options) =>
                    // A new selection has a new net income, so the amount
                    // to distribute starts again from the whole of it
                    setForm((prev) => ({
                      ...prev,
                      selectedRetainedNetIncomes: options || [],
                      amountToDistribute: null,
                    }))
                  }
                  placeholder="Select Retained Net Income"
                  styles={themeSelectStyles(
                    selectCustomStyles(
                      form.selectedRetainedNetIncomes.length > 0,
                      validated,
                    ),
                  )}
                  isLoading={isLoading}
                  isSearchable
                  isClearable
                  closeMenuOnSelect={false}
                  getOptionLabel={(option) =>
                    `${option.label} ${option.transaction_number || ""}`
                  }
                  formatOptionLabel={(option, { context }) =>
                    context === "menu" ? (
                      <div className="d-flex flex-column">
                        <span>{option.label}</span>
                        <small className="text-muted">
                          {option.transaction_number} ·{" "}
                          {formatPeriod(option.from, option.to)}
                        </small>
                      </div>
                    ) : (
                      option.label
                    )
                  }
                />
              </div>
            </div>
          </div>

          {form.selectedRetainedNetIncomes.length > 0 && (
            <div className="row mb-3">
              <div className="col-sm-6 mb-3 mb-sm-0">
                <Form.Label className="fw-bold">
                  Net Income Available
                </Form.Label>
                <Form.Control
                  type="text"
                  value={
                    isNetIncomeLoading
                      ? "Computing..."
                      : formatToPHP(netIncomeAvailable)
                  }
                  className={
                    !isNetIncomeLoading && netIncomeAvailable < 0
                      ? "text-danger"
                      : ""
                  }
                  readOnly
                  disabled
                />
              </div>
              <div className="col-sm-6">
                <Form.Label className="fw-bold">
                  Amount to Distribute <span className="text-danger">*</span>
                </Form.Label>
                <InputGroup hasValidation>
                  <InputGroup.Text>₱</InputGroup.Text>
                  <Form.Control
                    type="text"
                    inputMode="decimal"
                    value={formatNumberWithComma(form.amountToDistribute ?? "")}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    placeholder={isNetIncomeLoading ? "Computing..." : "0.00"}
                    disabled={!hasNetIncome}
                    isInvalid={Boolean(amountError)}
                  />
                  <Form.Control.Feedback type="invalid">
                    {amountError}
                  </Form.Control.Feedback>
                </InputGroup>
                {hasNetIncome && (
                  <Form.Text className="d-flex justify-content-between gap-2">
                    <span>
                      Shared to the owners; the rest stays in retained earnings.
                    </span>
                    {toCents(form.amountToDistribute) !==
                      toCents(netIncomeAvailable) && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-nowrap"
                        onClick={() =>
                          onFieldChange(
                            "amountToDistribute",
                            toAmountInput(netIncomeAvailable),
                          )
                        }>
                        Use all
                      </button>
                    )}
                  </Form.Text>
                )}
                {!isNetIncomeLoading && !hasNetIncome && (
                  <Form.Text className="text-danger">
                    There is no net income to distribute.
                  </Form.Text>
                )}
              </div>
            </div>
          )}

          <div className="row mb-3">
            <div className="col-sm-6 mb-3 mb-sm-0">
              <Form.Label className="fw-bold">Transaction Number</Form.Label>
              <Form.Control
                type="text"
                value={form.transactionNumber}
                readOnly
                disabled
              />
            </div>
            <div className="col-sm-6">
              <Form.Label className="fw-bold">
                Distribution Date <span className="text-danger">*</span>
              </Form.Label>
              <CustomDatePicker
                field="distributionDate"
                selected={
                  form.distributionDate ? new Date(form.distributionDate) : ""
                }
                handleDateChange={(date) =>
                  onFieldChange("distributionDate", date)
                }
                setter={setForm}
                CustomInput={TypeableDateInput}
                isRequired={true}
                validated={validated}
              />
            </div>
          </div>

          <div className="row mb-3">
            <div className="col-sm">
              <Form.Label className="fw-bold">
                Profit Distribution Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                value={form.distributionName}
                onChange={(e) =>
                  onFieldChange("distributionName", e.target.value)
                }
                placeholder="Enter profit distribution name"
                isInvalid={validated && !form.distributionName.trim()}
                required
              />
            </div>
          </div>

          <div className="row">
            <div className="col-sm">
              <Form.Label className="fw-bold">Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={form.remarks}
                onChange={(e) => onFieldChange("remarks", e.target.value)}
                placeholder="Enter remarks"
                style={{ resize: "none" }}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={isSaving}>
            Close
          </Button>
          <Button variant="primary" type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : submitLabel}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default AddProfitDistributionModal;
